/**
 * The /ai tab contract.
 *
 * Seven tabs (Ruby AI Actions spec 2026-09-27 §7). Routing became Ruby AI
 * Actions (D1), Usage became Usage & Margin, Credits became Ruby Tokens (D6),
 * and Coverage folded into Ruby AI Actions. Every old deep link — /ai-status,
 * /ai-usage and every retired ?tab= of /ai itself — resolves to the tab that
 * absorbed it. That mapping is pure so it is covered by tests.
 */

import {
  BarChart3, Zap, Sparkles, Palette, Activity, Gem, Settings2, Receipt,
  type LucideIcon,
} from 'lucide-react';

export const AI_ROUTE = '/ai';

export type AiTabId =
  | 'overview' | 'providers' | 'actions' | 'vibes' | 'usage' | 'ledger' | 'tokens' | 'settings';

// `ReceiptText` is not exported by the installed lucide-react — `Receipt` is
// the closest available mark for the Ledger tab.
export const AI_TABS: ReadonlyArray<{ id: AiTabId; label: string; icon: LucideIcon }> = [
  { id: 'overview',  label: 'Overview',           icon: BarChart3 },
  { id: 'providers', label: 'Providers & Models', icon: Zap },
  { id: 'actions',   label: 'Ruby AI Actions',    icon: Sparkles },
  { id: 'vibes',     label: 'Vibes',              icon: Palette },
  { id: 'usage',     label: 'Usage & Margin',     icon: Activity },
  { id: 'ledger',    label: 'Ledger',             icon: Receipt },
  { id: 'tokens',    label: 'Ruby Tokens',        icon: Gem },
  { id: 'settings',  label: 'Settings',           icon: Settings2 },
] as const;

const TAB_IDS = AI_TABS.map((t) => t.id) as AiTabId[];

export function isAiTabId(value: string | null | undefined): value is AiTabId {
  return !!value && (TAB_IDS as string[]).includes(value);
}

/** Retired /ai?tab= ids → the tab that absorbed them (2026-09-27). */
const RETIRED_AI_TAB_MAP: Record<string, AiTabId> = {
  routing: 'actions',    // D1: Ruby AI Actions is the upgraded Routing tab
  coverage: 'actions',   // folded into Ruby AI Actions (AI coverage section)
  credits: 'tokens',     // D6: credits are Ruby Tokens
};

/**
 * The tab to show for a raw ?tab= value: itself when current, the absorbing
 * tab when retired, otherwise Overview.
 */
export function normalizeAiTab(raw: string | null | undefined): AiTabId {
  if (isAiTabId(raw)) return raw;
  if (raw && RETIRED_AI_TAB_MAP[raw]) return RETIRED_AI_TAB_MAP[raw];
  return 'overview';
}

/** Old tab id → the tab that absorbed it. */
const LEGACY_TAB_MAP: Record<string, AiTabId> = {
  // /ai-status
  overview: 'overview',
  steps: 'settings',     // step costs moved into Settings
  tenants: 'usage',      // the usage report is pivotable by tenant
  providers: 'providers',
  settings: 'settings',
  // /ai-usage
  usage: 'usage',
  coverage: 'actions',   // coverage now lives inside Ruby AI Actions
  pricing: 'providers',  // unit prices live beside the models they price
  credits: 'tokens',     // credits are Ruby Tokens (D6)
  routing: 'actions',
};

export function resolveLegacyAiRedirect(pathname: string, search: string): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const fallback: AiTabId = pathname.startsWith('/ai-usage') ? 'usage' : 'overview';

  const rawTab = params.get('tab');
  const tenantId = params.get('tenantId');

  let tab: AiTabId;
  if (rawTab && LEGACY_TAB_MAP[rawTab]) tab = LEGACY_TAB_MAP[rawTab];
  else if (!rawTab && tenantId) tab = 'usage';
  else tab = fallback;

  const out = new URLSearchParams();
  out.set('tab', tab);
  // Only known params are forwarded — a stale utm_ or a hand-edited param
  // must not become part of the new page's contract.
  if (tenantId) out.set('tenantId', tenantId);
  return `${AI_ROUTE}?${out.toString()}`;
}
